import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type InvoiceDto, type InvoiceStatus } from '@shared';
import { apiGet, ApiError } from '../../lib/api';

type Filter = 'outstanding' | 'all' | 'paid';

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Outstanding',
  awaiting_wire: 'Awaiting wire',
  received: 'Received',
  cleared: 'Paid',
  void: 'Voided',
  refunded: 'Refunded',
};

const STATUS_TONE: Record<InvoiceStatus, string> = {
  draft: '',
  issued: 'amber',
  awaiting_wire: 'amber',
  received: 'accent',
  cleared: 'green',
  void: 'neutral',
  refunded: 'red',
};

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

function isOutstanding(s: InvoiceStatus): boolean {
  return s === 'issued' || s === 'awaiting_wire' || s === 'received';
}

export function PortalInvoices() {
  const [rows, setRows] = useState<InvoiceDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('outstanding');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ invoices: InvoiceDto[] }>('/api/invoices')
      .then((r) => setRows(r.invoices))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  const list = useMemo(() => {
    const all = rows ?? [];
    if (filter === 'outstanding') return all.filter((r) => isOutstanding(r.status));
    if (filter === 'paid') return all.filter((r) => r.status === 'cleared');
    return all;
  }, [rows, filter]);

  const outstanding = (rows ?? []).filter((r) => isOutstanding(r.status)).length;
  const outstandingTotalByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) {
      if (!isOutstanding(r.status)) continue;
      m.set(r.currency, (m.get(r.currency) ?? 0) + r.amount_pence);
    }
    return m;
  }, [rows]);

  return (
    <>
      <div>
        <h1 className="portal-h1">Invoices</h1>
        <p className="portal-sub">
          Engagement invoices from Omega. Pay by card or bank transfer — open an invoice to choose.
          Card payments clear instantly; bank transfers reconcile when funds arrive.
        </p>
      </div>

      {outstanding > 0 && (
        <div className="portal-cards" style={{ marginTop: 20 }}>
          {Array.from(outstandingTotalByCurrency.entries()).map(([ccy, amt]) => (
            <div className="card" key={ccy}>
              <div className="lbl">Outstanding ({ccy})</div>
              <div className="val">{formatMoney(amt, ccy)}</div>
              <div className="delta">{outstanding} invoice{outstanding === 1 ? '' : 's'} open</div>
            </div>
          ))}
        </div>
      )}

      <div className="tabs" style={{ marginTop: 24 }}>
        <button className={filter === 'outstanding' ? 'on' : ''} onClick={() => setFilter('outstanding')}>
          Outstanding {outstanding > 0 && <span className="tab-count">{outstanding}</span>}
        </button>
        <button className={filter === 'paid' ? 'on' : ''} onClick={() => setFilter('paid')}>Paid</button>
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>All</button>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {rows !== null && !error && list.length === 0 && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--fg-mute)' }}>
          {filter === 'outstanding' ? 'No outstanding invoices — you\'re all settled.' : 'No invoices in this view.'}
        </div>
      )}

      <div className="proposal-grid">
        {list.map((inv) => (
          <Link key={inv.id} to={`/portal/invoices/${inv.id}`} className="card proposal-card proposal-card-link">
            <header className="proposal-hd">
              <div>
                <div className="rid">{inv.id}</div>
                <div className="proposal-ref">Proposal {inv.proposal_id}</div>
              </div>
              <span className={`chip ${STATUS_TONE[inv.status]}`}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                {STATUS_LABEL[inv.status]}
              </span>
            </header>

            <div className="proposal-amount">{formatMoney(inv.amount_pence, inv.currency)}</div>
            {inv.milestone_label && (
              <div className="proposal-meta">
                <span>Milestone</span>
                <strong>{inv.milestone_label}</strong>
              </div>
            )}
            <div className="proposal-meta">
              <span>Due</span>
              <strong>{inv.due_at.slice(0, 10)}</strong>
            </div>
            {inv.paid_at && (
              <div className="proposal-meta">
                <span>Paid</span>
                <strong>{inv.paid_at.slice(0, 10)}</strong>
              </div>
            )}
          </Link>
        ))}
      </div>
    </>
  );
}
